import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router";
import { Avatar } from "../components/Avatar";
import { Sheet } from "../components/Sheet";
import { Toast } from "../components/Toast";
import {
  SHARE_DAYS,
  addPerson,
  connectReturn,
  deletePerson,
  disconnectGoogle,
  fetchGoogleContacts,
  fetchPeople,
  filterContacts,
  importGoogleContacts,
  importMessage,
  selectedForImport,
  shareLink,
  startGoogleConnect,
  syncGooglePhotos,
  syncMessage,
  toggleContact,
  updatePerson,
  type GoogleContactRow,
  type PeopleDto,
  type PersonRow,
} from "../lib/peoplePage";

const input = "rounded-lg border border-line bg-surface px-2.5 py-2 text-[15px] text-neutral-100";
const small = "min-h-9 rounded-lg border border-line px-3 text-[13px] disabled:opacity-40";
const primary = "min-h-10 rounded-lg bg-neutral-100 px-3.5 text-sm font-semibold text-ground disabled:opacity-40";
const message = (e: unknown) => (e instanceof Error ? e.message : String(e));

/** Rows rendered at once; a filter narrows the rest. Thousands of "other contacts" is normal. */
const IMPORT_LIST_LIMIT = 150;

function ShareSheet({ p, configured, onClose }: { p: PersonRow; configured: boolean; onClose: () => void }) {
  const [days, setDays] = useState<number>(30);
  const [url, setUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  return (
    <Sheet title={`Share ${p.name}'s games`} onClose={onClose}>
      <div className="grid gap-3">
        <p className="text-sm text-muted">
          A read-only link to every game you went to with {p.name}. Whoever has it can't sign in or see anything else, and it stops working when it expires.
        </p>
        {!configured && <p className="text-sm text-bad">Sharing isn't configured on this server (SHARE_LINK_SECRET is missing).</p>}
        <div className="flex flex-wrap gap-1.5">
          {SHARE_DAYS.map((d) => (
            <button key={d} type="button" aria-pressed={days === d} onClick={() => { setDays(d); setUrl(null); }} className={`rounded-full border px-3 py-1 text-[13px] ${days === d ? "border-neutral-100 bg-neutral-100 font-semibold text-ground" : "border-line"}`}>
              Expires in {d} days
            </button>
          ))}
        </div>
        {url && <p className="rounded-lg border border-line bg-ground p-2.5 text-[12.5px] break-all text-accent">{url}</p>}
        {error && <p className="text-sm text-bad">{error}</p>}
        <div className="flex justify-end gap-2">
          {url && (
            <a href={url} target="_blank" rel="noreferrer" className="grid min-h-10 place-items-center rounded-lg border border-line px-3.5 text-sm">
              Preview
            </a>
          )}
          <button
            type="button"
            disabled={!configured}
            onClick={async () => {
              setError(null);
              try {
                const link = url ?? (await shareLink(p.id, days));
                setUrl(link);
                await navigator.clipboard?.writeText(link).catch(() => undefined);
              } catch (e) {
                setError(message(e));
              }
            }}
            className={primary}
          >
            {url ? "Copy link" : "Make link"}
          </button>
        </div>
      </div>
    </Sheet>
  );
}

function EditSheet({ p, onClose, onDone }: { p: PersonRow | null; onClose: () => void; onDone: (m: string) => void }) {
  const [name, setName] = useState(p?.name ?? "");
  const [email, setEmail] = useState(p?.email ?? "");
  const [hide, setHide] = useState(p?.hide_on_share ?? false);
  const [confirm, setConfirm] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const run = (op: Promise<unknown>, m: string) => op.then(() => onDone(m)).catch((e: unknown) => setError(message(e)));
  return (
    <Sheet title={p ? `Edit ${p.name}` : "Add person"} onClose={onClose}>
      <div className="grid gap-3">
        <label className="grid gap-1 text-xs text-muted">
          Name
          <input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Jordan" className={input} />
        </label>
        <label className="grid gap-1 text-xs text-muted">
          Email (optional, for a Gravatar photo)
          <input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="jordan@example.com" className={input} />
        </label>
        {p && (
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={hide} onChange={(e) => setHide(e.target.checked)} />
            Hide on shared links (still shown to you)
          </label>
        )}
        {error && <p className="text-sm text-bad">{error}</p>}
        {confirm && p ? (
          <div className="grid gap-2">
            <p className="text-sm">
              {p.events
                ? `${p.name} is tagged on ${p.events} ${p.events === 1 ? "game" : "games"}. Deleting removes those tags.`
                : `Delete ${p.name}?`}
            </p>
            <div className="flex justify-end gap-2">
              <button type="button" onClick={() => setConfirm(false)} className="min-h-10 rounded-lg border border-line px-3.5 text-sm">
                Keep
              </button>
              <button type="button" onClick={() => run(deletePerson(p.id), `Deleted ${p.name}`)} className="min-h-10 rounded-lg bg-bad px-3.5 text-sm font-semibold text-ground">
                Delete {p.name}
              </button>
            </div>
          </div>
        ) : (
          <div className="flex justify-end gap-2">
            {p && (
              <button type="button" onClick={() => setConfirm(true)} className="mr-auto min-h-10 rounded-lg border border-line px-3.5 text-sm text-bad">
                Delete
              </button>
            )}
            <button
              type="button"
              disabled={!name.trim()}
              onClick={() =>
                run(
                  p ? updatePerson(p.id, { name: name.trim(), email: email.trim(), hide_on_share: hide }) : addPerson(name.trim(), email.trim()),
                  p ? "Saved" : `Added ${name.trim()}`,
                )
              }
              className={primary}
            >
              {p ? "Save" : "Add"}
            </button>
          </div>
        )}
      </div>
    </Sheet>
  );
}

/** Pick Google contacts to add as people. Already-added ones show, unselectable. */
function ImportSheet({ onClose, onDone }: { onClose: () => void; onDone: (m: string) => void }) {
  const [rows, setRows] = useState<GoogleContactRow[] | null>(null);
  const [refreshes, setRefreshes] = useState(0);
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    fetchGoogleContacts(refreshes > 0)
      .then((r) => !cancelled && setRows(r))
      .catch((e: unknown) => !cancelled && setError(message(e)));
    return () => {
      cancelled = true;
    };
  }, [refreshes]);

  const shown = useMemo(() => filterContacts(rows ?? [], query), [rows, query]);
  const picked = selectedForImport(selected, rows ?? []);

  return (
    <Sheet title="Import contacts" onClose={onClose}>
      <div className="grid gap-3">
        <div className="flex gap-2">
          <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Filter by name or email" aria-label="Filter contacts" className={`${input} min-w-0 flex-1`} />
          <button
            type="button"
            disabled={rows === null}
            onClick={() => {
              setRows(null);
              setError(null);
              setRefreshes((n) => n + 1);
            }}
            className={small}
          >
            Refresh
          </button>
        </div>
        {error && <p className="text-sm text-bad">{error}</p>}
        {rows === null && !error && <p className="text-sm text-muted">Loading contacts from Google…</p>}
        {rows !== null && (
          <div>
            {shown.slice(0, IMPORT_LIST_LIMIT).map((r) => (
              <label key={r.resource_name} className={`flex items-center gap-3 border-b border-line/60 py-2 ${r.already_added ? "opacity-50" : ""}`}>
                <input
                  type="checkbox"
                  disabled={r.already_added}
                  checked={r.already_added || selected.has(r.resource_name)}
                  onChange={() => setSelected((s) => toggleContact(s, r))}
                />
                <Avatar name={r.name} url={r.photo_url ?? undefined} size={32} />
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm font-semibold">{r.name}</div>
                  <div className="truncate text-[12.5px] text-faint">
                    {r.already_added ? "Already added" : ""}
                    {r.already_added && r.email ? " · " : ""}
                    {r.email ?? ""}
                  </div>
                </div>
              </label>
            ))}
            {shown.length > IMPORT_LIST_LIMIT && (
              <p className="pt-2 text-[12.5px] text-faint">
                Showing {IMPORT_LIST_LIMIT} of {shown.length}. Filter to find the rest.
              </p>
            )}
            {shown.length === 0 && <p className="text-sm text-muted">{rows.length ? "No contact matches that filter." : "No contacts came back from Google."}</p>}
          </div>
        )}
        <div className="flex justify-end">
          <button
            type="button"
            disabled={!picked.length || busy}
            onClick={() => {
              setBusy(true);
              setError(null);
              importGoogleContacts(picked)
                .then((r) => onDone(importMessage(r)))
                .catch((e: unknown) => {
                  setBusy(false);
                  setError(message(e));
                });
            }}
            className={primary}
          >
            {busy ? "Adding…" : `Add ${picked.length}`}
          </button>
        </div>
      </div>
    </Sheet>
  );
}

/** Connect, sync photos, import, disconnect. The refresh token never reaches this page. */
function GooglePanel({
  google,
  returnDetail,
  onImport,
  onDone,
}: {
  google: PeopleDto["google"];
  returnDetail: string | null;
  onImport: () => void;
  onDone: (m: string) => void;
}) {
  const [busy, setBusy] = useState<"connect" | "sync" | "disconnect" | null>(null);
  const [confirm, setConfirm] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const shownError = error ?? returnDetail;
  const fail = (e: unknown) => {
    setBusy(null);
    setError(message(e));
  };

  if (!google) {
    return (
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2 rounded-xl bg-surface px-3.5 py-3 text-sm">
        <span className="min-w-0 flex-1 text-muted">Google Contacts isn't connected. Connect it to import names and profile photos.</span>
        <button
          type="button"
          disabled={busy !== null}
          onClick={() => {
            setBusy("connect");
            setError(null);
            // A full-page navigation: Google sends the browser back to the
            // API's callback, which redirects to /people?google=...
            startGoogleConnect().then((url) => window.location.assign(url), fail);
          }}
          className={primary}
        >
          {busy === "connect" ? "Opening Google…" : "Connect Google"}
        </button>
        {shownError && <p className="basis-full text-sm text-bad">{shownError}</p>}
      </div>
    );
  }

  return (
    <div className="grid gap-2.5 rounded-xl bg-surface px-3.5 py-3 text-sm">
      <div>
        Google Contacts connected{google.user_email ? ` as ${google.user_email}` : ""}.
      </div>
      {confirm ? (
        <div className="grid gap-2">
          <p className="text-sm">Disconnect Google Contacts? People and photos already imported stay.</p>
          <div className="flex justify-end gap-2">
            <button type="button" onClick={() => setConfirm(false)} className="min-h-10 rounded-lg border border-line px-3.5 text-sm">
              Keep
            </button>
            <button
              type="button"
              disabled={busy !== null}
              onClick={() => {
                setBusy("disconnect");
                setError(null);
                disconnectGoogle().then(() => onDone("Google Contacts disconnected"), fail);
              }}
              className="min-h-10 rounded-lg bg-bad px-3.5 text-sm font-semibold text-ground disabled:opacity-40"
            >
              Disconnect
            </button>
          </div>
        </div>
      ) : (
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            disabled={busy !== null}
            onClick={() => {
              setBusy("sync");
              setError(null);
              syncGooglePhotos().then((r) => {
                setBusy(null);
                onDone(syncMessage(r));
              }, fail);
            }}
            className={small}
          >
            {busy === "sync" ? "Syncing…" : "Sync photos"}
          </button>
          <button type="button" disabled={busy !== null} onClick={onImport} className={small}>
            Import contacts
          </button>
          <button type="button" disabled={busy !== null} onClick={() => setConfirm(true)} className={`${small} ml-auto text-bad`}>
            Disconnect
          </button>
        </div>
      )}
      {shownError && <p className="text-sm text-bad">{shownError}</p>}
    </div>
  );
}

/**
 * People: who you go to games with. Admin, so it lives behind the header
 * icon, not a tab. Google Contacts connects through a redirect to Google that
 * lands back here as `?google=connected` (or `?google=error&reason=...`),
 * which is read once, toasted, and stripped from the URL.
 */
export default function People() {
  const [dto, setDto] = useState<PeopleDto | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [reloads, setReloads] = useState(0);
  const [sheet, setSheet] = useState<{ kind: "share" | "edit"; p: PersonRow } | "add" | "import" | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [returnDetail, setReturnDetail] = useState<string | null>(null);
  const [sp, setSp] = useSearchParams();
  const reload = useCallback(() => setReloads((n) => n + 1), []);
  const closeToast = useCallback(() => setToast(null), []);

  useEffect(() => {
    const back = connectReturn(sp);
    if (!back) return;
    setToast(back.toast);
    setReturnDetail(back.detail);
    const rest = new URLSearchParams(sp);
    rest.delete("google");
    rest.delete("reason");
    // replace: Back must not return to the connect result.
    setSp(rest, { replace: true });
  }, [sp, setSp]);

  useEffect(() => {
    let cancelled = false;
    fetchPeople()
      .then((d) => {
        if (cancelled) return;
        setDto(d);
        setError(null);
      })
      .catch((e: unknown) => !cancelled && setError(message(e)));
    return () => {
      cancelled = true;
    };
  }, [reloads]);

  if (error && !dto) return <div className="rounded-lg border border-bad/50 bg-bad/10 p-4 text-sm">Could not load people: {error}</div>;
  if (!dto) return <p className="text-sm text-muted">Loading…</p>;
  const done = (m: string) => {
    setSheet(null);
    setToast(m);
    setReturnDetail(null);
    reload();
  };

  return (
    <section className="grid gap-4">
      <div className="flex flex-wrap items-baseline gap-2.5">
        <h1 className="font-display text-3xl leading-none font-bold uppercase">People</h1>
        <span className="text-sm text-muted">
          {dto.people.length} {dto.people.length === 1 ? "person" : "people"}
        </span>
        <button type="button" onClick={() => setSheet("add")} className="ml-auto min-h-10 rounded-lg bg-neutral-100 px-3.5 text-sm font-semibold text-ground">
          ＋ Add
        </button>
      </div>
      {/* Keyed on the connection, so connecting or disconnecting starts the panel afresh. */}
      <GooglePanel
        key={dto.google ? "connected" : "disconnected"}
        google={dto.google}
        returnDetail={returnDetail}
        onImport={() => setSheet("import")}
        onDone={done}
      />
      <div>
        {dto.people.map((p) => (
          <div key={p.id} className="flex items-center gap-3 border-b border-line/60 py-2.5">
            <Avatar name={p.name} url={p.avatar_url ?? undefined} size={40} />
            <div className="min-w-0 flex-1">
              <div className="truncate font-semibold">{p.name}</div>
              <div className="truncate text-[12.5px] text-faint">
                {p.events} {p.events === 1 ? "game" : "games"} together
                {p.linked_to_google ? " · Google contact" : p.email ? ` · ${p.email}` : ""}
                {p.hide_on_share ? " · hidden on share links" : ""}
              </div>
            </div>
            <button type="button" onClick={() => setSheet({ kind: "share", p })} className="min-h-9 rounded-lg border border-line px-3 text-[13px]">
              Share
            </button>
            <button type="button" onClick={() => setSheet({ kind: "edit", p })} className="min-h-9 rounded-lg border border-line px-3 text-[13px]">
              Edit
            </button>
          </div>
        ))}
        {dto.people.length === 0 && <p className="text-sm text-muted">No people yet. Add someone, or connect Google Contacts and import them.</p>}
      </div>
      {sheet === "add" && <EditSheet p={null} onClose={() => setSheet(null)} onDone={done} />}
      {sheet === "import" && <ImportSheet onClose={() => setSheet(null)} onDone={done} />}
      {sheet && typeof sheet === "object" && sheet.kind === "edit" && <EditSheet p={sheet.p} onClose={() => setSheet(null)} onDone={done} />}
      {sheet && typeof sheet === "object" && sheet.kind === "share" && <ShareSheet p={sheet.p} configured={dto.share_configured} onClose={() => setSheet(null)} />}
      {toast && <Toast text={toast} onDone={closeToast} />}
    </section>
  );
}
